import { useLingui, Trans } from '@lingui/react/macro';
import * as Tabs from '@radix-ui/react-tabs';
import { Blocks, Globe, GraduationCap, Plug, FormInput } from 'lucide-react';

import { ResourcePage } from '@/components/page/ResourcePage';
import { ClientSideFormsTab } from '@/features/client-side-tools/components/ClientSideFormsTab';

import type { AgentToolsTabId } from './agentToolsTabs';
import { BuiltinToolsTab } from './components/BuiltinToolsTab';
import { HttpToolsTab } from './components/HttpToolsTab';
import { McpIntegrationsTab } from './components/McpIntegrationsTab';
import { SkillsTab } from './components/SkillsTab';

const tabTriggerClass =
  'flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-fg data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-fg';

export interface AgentToolsPageProps {
  tab: AgentToolsTabId;
  onTabChange: (tab: AgentToolsTabId) => void;
}

export function AgentToolsPage({ tab, onTabChange }: AgentToolsPageProps) {
  const { t } = useLingui();

  return (
    <ResourcePage
      title={t`Agent Tools`}
      breadcrumbs={[{ label: t`Agent Tools`, to: '/agent-tools' }]}
    >
      <Tabs.Root
        value={tab}
        onValueChange={value => onTabChange(value as AgentToolsTabId)}
        className='flex min-h-0 flex-col'
      >
        <Tabs.List className='mb-4 flex gap-2 border-b border-border'>
          <Tabs.Trigger value='builtin' className={tabTriggerClass}>
            <Blocks className='size-4' />
            <Trans>Built-in</Trans>
          </Tabs.Trigger>
          <Tabs.Trigger value='mcp' className={tabTriggerClass}>
            <Plug className='size-4' />
            <Trans>MCP Integrations</Trans>
          </Tabs.Trigger>
          <Tabs.Trigger value='http' className={tabTriggerClass}>
            <Globe className='size-4' />
            <Trans>HTTP Tools</Trans>
          </Tabs.Trigger>
          <Tabs.Trigger value='client-side' className={tabTriggerClass}>
            <FormInput className='size-4' />
            <Trans>Form tools</Trans>
          </Tabs.Trigger>
          <Tabs.Trigger value='skills' className={tabTriggerClass}>
            <GraduationCap className='size-4' />
            <Trans>Skills</Trans>
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value='builtin'>
          <BuiltinToolsTab />
        </Tabs.Content>

        <Tabs.Content value='mcp'>
          <McpIntegrationsTab />
        </Tabs.Content>

        <Tabs.Content value='http'>
          <HttpToolsTab />
        </Tabs.Content>

        <Tabs.Content value='client-side'>
          <ClientSideFormsTab />
        </Tabs.Content>

        <Tabs.Content value='skills'>
          <SkillsTab />
        </Tabs.Content>
      </Tabs.Root>
    </ResourcePage>
  );
}
